import { Controller } from "@hotwired/stimulus"

// Master–detail selection for the Conversion Actions page. Each list row's body is a
// turbo-frame link that loads its detail into the shared ca-detail-frame; this controller
// tracks which row is selected so it reads as active. Loading is Turbo's job.
//
// selectedIdValue persists on the controller element (ca-management-panel-content-*), which
// the per-CA / funnel turbo-stream updates never replace — so when a row (or the whole list)
// re-renders after a toggle, rowTargetConnected re-applies the highlight to the new element.
export default class extends Controller {
  static targets = ["row"]
  static values = { selectedId: Number }

  connect() {
    // Below lg the detail is a drawer; if it's left open when the viewport grows to desktop, close it
    // so the shared drawer-instance releases the <html> scroll-lock (mirrors mobile_nav#closeOnDesktop).
    this.desktopQuery = window.matchMedia("(min-width: 1024px)")
    this.closeOnDesktop = (e) => { if (e.matches) window.dispatchEvent(new CustomEvent("drawer:close")) }
    this.desktopQuery.addEventListener("change", this.closeOnDesktop)
  }

  disconnect() {
    this.desktopQuery?.removeEventListener("change", this.closeOnDesktop)
  }

  select(event) {
    const row = event.currentTarget.closest("[data-conversion-actions--master-detail-target='row']")
    if (!row) return
    this.selectedIdValue = Number(row.dataset.conversionActionId)
    this.rowTargets.forEach((r) => r.toggleAttribute("data-selected", r === row))
    this.openDrawerOnNarrow(event.currentTarget)
  }

  // Below lg the detail column is a right-side slide-over — pop it on a user row-click so the settings
  // aren't stacked below the list. Desktop keeps the sticky column (no drawer). Reuses the shared
  // drawer-instance: the frame id maps 1:1 to the drawer id per ad account.
  openDrawerOnNarrow(link) {
    if (!window.matchMedia("(max-width: 1023.98px)").matches) return
    const frameId = link.getAttribute("data-turbo-frame") // ca-detail-frame-<ad_account>
    if (!frameId) return
    // -panel suffix: drawer-instance matches loosely, so an unsuffixed "…-42" would also open "…-425".
    const instance = frameId.replace("ca-detail-frame-", "ca-detail-drawer-") + "-panel"
    window.dispatchEvent(new CustomEvent("drawer:open", { detail: { instance } }))
  }

  rowTargetConnected(row) {
    // First load (nothing selected yet): auto-select the first row and load its detail —
    // the design opens on the first action. Re-renders after a toggle already have a
    // selectedId, so they fall through to just re-applying the highlight.
    if (!this.hasSelectedIdValue) {
      this.selectedIdValue = Number(row.dataset.conversionActionId)
      row.toggleAttribute("data-selected", true)
      // The row's anchor carries this panel's ad-account-scoped frame id (each provider tab has
      // its own ca-detail-frame-<ad_account>), so read it from there rather than a constant.
      const link = row.querySelector("a[data-turbo-frame]")
      const href = link?.getAttribute("href")
      const frame = document.getElementById(link?.getAttribute("data-turbo-frame"))
      if (href && frame) frame.setAttribute("src", href)
      return
    }
    row.toggleAttribute("data-selected", Number(row.dataset.conversionActionId) === this.selectedIdValue)
  }
}
